import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Layout, Trash2 } from 'lucide-react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { toast } from 'sonner';

interface CanvasToolbarProps {
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  zoom?: number;
}

export function CanvasToolbar({ onZoomIn, onZoomOut, zoom = 100 }: CanvasToolbarProps) {
  const { currentWorkflow, selectNode } = useWorkflowStore();

  const handleAutoLayout = () => {
    if (!currentWorkflow || currentWorkflow.nodes.length === 0) {
      toast.info('Add nodes first');
      return;
    }

    // Simple grid layout
    const cols = Math.ceil(Math.sqrt(currentWorkflow.nodes.length));
    const spacing = 250;

    currentWorkflow.nodes.forEach((node, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      useWorkflowStore.setState((state) => {
        if (!state.currentWorkflow) return state;
        return {
          currentWorkflow: {
            ...state.currentWorkflow,
            nodes: state.currentWorkflow.nodes.map((n) =>
              n.id === node.id
                ? { ...n, x: col * spacing, y: row * spacing + 50 }
                : n
            ),
          },
        };
      });
    });

    selectNode(null);
    toast.success('Layout applied');
  };

  const handleClearCanvas = () => {
    if (!currentWorkflow || currentWorkflow.nodes.length === 0) {
      toast.info('Canvas is empty');
      return;
    }

    if (confirm('Clear all nodes? This cannot be undone.')) {
      useWorkflowStore.setState((state) => {
        if (!state.currentWorkflow) return state;
        return {
          currentWorkflow: {
            ...state.currentWorkflow,
            nodes: [],
            edges: [],
          },
        };
      });
      selectNode(null);
      toast.success('Canvas cleared');
    }
  };

  return (
    <div className="absolute top-4 right-4 flex gap-2 bg-card border border-border rounded-lg shadow-lg p-2">
      <Button
        variant="ghost"
        size="sm"
        title="Zoom in"
        onClick={onZoomIn}
      >
        <ZoomIn className="w-4 h-4" />
      </Button>
      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
        {zoom}%
      </div>
      <Button
        variant="ghost"
        size="sm"
        title="Zoom out"
        onClick={onZoomOut}
      >
        <ZoomOut className="w-4 h-4" />
      </Button>
      <div className="w-px bg-border" />
      <Button
        variant="ghost"
        size="sm"
        title="Auto layout"
        onClick={handleAutoLayout}
      >
        <Layout className="w-4 h-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        title="Clear canvas"
        onClick={handleClearCanvas}
        className="text-destructive hover:text-destructive"
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}
